import React from 'react'
import './polyfills'
import { ApolloProvider } from '@apollo/client'
import { ChakraProvider } from '@chakra-ui/react'
import { css, Global } from '@emotion/react'
import { Elements } from '@stripe/react-stripe-js'
import 'react-dates/initialize'
// Global vendor stylesheets, imported once at the entry so they always live in
// the main chunk. They used to be pulled in only by lazy route chunks (the
// date pickers, the slideshow, the flexbox grid), and whenever two chunks
// imported them in different orders mini-css-extract-plugin raised a
// "Conflicting order" warning — which CI (CI=true) escalates to a build
// failure. Their relative order is genuinely irrelevant: each targets its own
// selector namespace (.DayPicker*, .swiper-*, .row/.col-*), and all app
// styling is Chakra/Emotion, which never passes through the CSS extractor.
import 'react-dates/lib/css/_datepicker.css'
import 'flexboxgrid2/flexboxgrid2.css'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import { createRoot } from 'react-dom/client'
import { Helmet } from 'react-helmet'
import client from './apollo'
import { getStripe } from './lib/stripe'
import App from './App'
import { APP_NAME } from './constants'
import { AppProvider, AuthProvider, PreviewerProvider, VideoModalProvider } from './context'
import * as serviceWorker from './serviceWorker'
import { theme } from './theme'
import { disableReactDevTools } from './utils/index'
import posthog from 'posthog-js'

// The root Elements provider read `REACT_APP_STRIPE_API_KEY`, which is not a
// variable this app defines — the publishable key is `REACT_APP_STRIPE_PUBLISHABLE_KEY`.
// So this provider was always constructed with an empty key, and any screen
// relying on it (rather than mounting its own <Elements>) had no working Stripe
// instance at all. `getStripe` is the single, memoised resolver used everywhere.
const stripePromise = getStripe()
posthog.init(
  process.env.NODE_ENV === 'production'
    ? 'phc_FJTHAtI4Nc2AAKfrfGp2le7PGuxTQ7fDeKVxWeJutXx'
    : 'phc_buOFrinwCnvsUE8yMNFqYmij6a7h0sEeVuU9UuWelBV',
  {
    api_host: 'https://app.posthog.com'
  }
)

if (process.env.NODE_ENV === 'production') {
  disableReactDevTools()
}

// React 18's concurrent root. The app previously mounted through the legacy
// `ReactDOM.render`, which opts the whole tree out of React 18 — most visibly
// out of automatic batching, so every `setState` outside a React event handler
// (Apollo query results, socket events, timers, promise callbacks) triggered
// its own synchronous re-render of the subtree. Dashboards here fire a dozen
// queries at once, so that was a dozen renders where one would do.
const container = document.getElementById('root')

if (!container) {
  throw new Error('Root element #root is missing from index.html — the app cannot mount.')
}

createRoot(container).render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <ChakraProvider theme={theme}>
        <Elements stripe={stripePromise}>
          <AuthProvider>
            <Helmet titleTemplate={`${APP_NAME} - %s`} />

            <AppProvider>
              <VideoModalProvider>
                <PreviewerProvider>
                  <Global
                    styles={css`
                      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap');

                      /* ── Aurant Base Reset ────────────────────── */
                      * {
                        font-family: ${theme.fonts.body};
                        box-sizing: border-box;
                      }
                      *:focus {
                        box-shadow: none !important;
                        outline: none;
                      }
                      ::placeholder {
                        font-size: small;
                        opacity: 1;
                        color: #a3a3a3;
                      }

                      /* ── Root & Body ───────────────────────────── */
                      html {
                        background-color: ${theme.colors.background[100]};
                        -webkit-font-smoothing: antialiased;
                        -moz-osx-font-smoothing: grayscale;
                        scroll-behavior: smooth;
                      }
                      body {
                        overflow-x: hidden;
                        line-height: 1.7;
                      }

                      /* ── Links ──────────────────────────────────── */
                      a {
                        text-decoration: none;
                        transition: color 0.2s ease;
                      }
                      a:focus,
                      a:focus-visible {
                        outline: none;
                      }

                      /* ── Buttons ────────────────────────────────── */
                      button {
                        cursor: pointer;
                        border-radius: 0;
                      }

                      /* ── Form Elements ──────────────────────────── */
                      input,
                      textarea,
                      select {
                        border-radius: 0 !important;
                      }
                      input:focus,
                      textarea:focus,
                      select:focus {
                        outline: none;
                        box-shadow: none;
                      }

                      /* ── Aurant Scrollbar ───────────────────────── */
                      ::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                      }
                      ::-webkit-scrollbar-track {
                        background: transparent;
                      }
                      ::-webkit-scrollbar-thumb {
                        background-color: #d4d4d4;
                        border-radius: 3px;
                      }
                      ::-webkit-scrollbar-thumb:hover {
                        background-color: #a3a3a3;
                      }

                      /* ── Selection ──────────────────────────────── */
                      ::selection {
                        background-color: #171717;
                        color: white;
                      }

                      /* ── Swiper Overrides ───────────────────────── */
                      .swiper-pagination-bullet-active {
                        background-color: #171717 !important;
                      }
                      .swiper-pagination-bullet {
                        border-radius: 0 !important;
                        width: 8px !important;
                        height: 8px !important;
                      }

                      /* ── Aurant Transitions ────────────────────── */
                      img {
                        transition: opacity 0.3s ease;
                      }
                    `}
                  />
                  <App />
                </PreviewerProvider>
              </VideoModalProvider>
            </AppProvider>
          </AuthProvider>
        </Elements>
      </ChakraProvider>
    </ApolloProvider>
  </React.StrictMode>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
